import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import type { PendingGeneration } from '@/types';

import useWardrobeStore from './wardrobe';
import type { IWardrobeStore } from './wardrobe';

export type ToolPriceStatus = 'idle' | 'loading' | 'ready' | 'error';

interface ICreditsStore {
  toolPrice: number;
  toolPriceStatus: ToolPriceStatus;
  availableCredits: number;
  availableCreditsLoaded: boolean;
}

export interface IAppStore extends IWardrobeStore, ICreditsStore {}

interface PersistedSlice {
  library: { pendingGenerations: PendingGeneration[] };
}

const _store = create<IAppStore>()(
  persist(
    (...props) => ({
      ...useWardrobeStore(...props),
      toolPrice: 0,
      toolPriceStatus: 'idle',
      availableCredits: 0,
      availableCreditsLoaded: false,
    }),
    {
      name: 'ai-wardrobe-pending',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state): PersistedSlice => ({
        library: { pendingGenerations: state.library.pendingGenerations },
      }),
      merge: (persisted, current) => {
        const p = persisted as PersistedSlice | undefined;
        const restored = p?.library?.pendingGenerations ?? [];
        return {
          ...current,
          library: { ...current.library, pendingGenerations: restored },
        };
      },
    },
  ),
);

export function useAppStore<T>(selector: (s: IAppStore) => T): T {
  return _store(useShallow(selector));
}

export const getAppStore = _store.getState;
export const setAppStore = _store.setState;
